import { createContext, FC, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { reactContextErrors } from '../constants/errorMessages';
import type { FilterSelection } from '../pages/market-place/MarketPlaceFilters';
import {
  addOrUpdateGuestMarketplaceCartLine,
  getGuestMarketplaceCartTotalQuantity,
  GuestMarketplaceCartLine,
} from '../services/guestMarketplaceCart.service';
import { MarketPlaceSpecimenResponse } from '../types/interfaces/marketPlace.interfaces';
import { Genders } from '../types/enums';

type GuestMarketplaceContextValue = {
  activeFilters: FilterSelection | undefined;
  setActiveFilters: (filters: FilterSelection) => void;
  listingProductCount: number;
  setListingProductCount: (count: number) => void;
  guestCartItemCount: number;
  refreshGuestCartCount: () => void;
  addGuestSpecimenToCart: (specimen: MarketPlaceSpecimenResponse, quantity: number) => void;
};

const GuestMarketplaceContext = createContext<GuestMarketplaceContextValue | null>(null);

export const GuestMarketplaceProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [activeFilters, setActiveFilters] = useState<FilterSelection | undefined>(undefined);
  const [listingProductCount, setListingProductCount] = useState<number>(0);
  const [guestCartItemCount, setGuestCartItemCount] = useState<number>(() => getGuestMarketplaceCartTotalQuantity());

  const refreshGuestCartCount = useCallback(() => {
    setGuestCartItemCount(getGuestMarketplaceCartTotalQuantity());
  }, []);

  const addGuestSpecimenToCart = useCallback(
    (specimen: MarketPlaceSpecimenResponse, quantity: number) => {
      const isFemale = specimen.animal?.gender === Genders.Female;
      const line: GuestMarketplaceCartLine = {
        marketPlaceSpecimenId: specimen.marketPlaceSpecimenId,
        quantity,
        sellerAccountId: specimen.accountId,
        specimenId: specimen.specimenId,
        ...(isFemale ? { embryoStrawId: specimen.embryoStrawId ?? 0 } : {}),
      };
      addOrUpdateGuestMarketplaceCartLine(line);
      refreshGuestCartCount();
    },
    [refreshGuestCartCount],
  );

  const value = useMemo(
    () => ({
      activeFilters,
      setActiveFilters,
      listingProductCount,
      setListingProductCount,
      guestCartItemCount,
      refreshGuestCartCount,
      addGuestSpecimenToCart,
    }),
    [activeFilters, listingProductCount, guestCartItemCount, refreshGuestCartCount, addGuestSpecimenToCart],
  );

  return <GuestMarketplaceContext.Provider value={value}>{children}</GuestMarketplaceContext.Provider>;
};

export const useGuestMarketplace = (): GuestMarketplaceContextValue => {
  const context = useContext(GuestMarketplaceContext);
  if (!context) {
    throw new Error(reactContextErrors.USE_GUEST_MARKETPLACE_WITHIN_PROVIDER);
  }
  return context;
};

export const useGuestMarketplaceOptional = (): GuestMarketplaceContextValue | null => {
  return useContext(GuestMarketplaceContext);
};
